import { h, type ComponentChildren, type JSX } from 'preact'
import { useLayoutEffect, useRef } from 'preact/hooks'
import { useScope } from './scope.tsx'
import { activationCause, spring, tracker, type Motion, type MotionCause } from './spring.ts'
import { duration, layout } from './tokens.ts'
import './gestures.css'
export const LONG_PRESS = duration['long-press']
type Point = JSX.TargetedPointerEvent<HTMLElement>
type Press = { currentTarget: HTMLElement; pointerId: number }

/** Global listeners exist only between down and release, never while a row is idle. */
function pointers(pointerId: number, move: (event: PointerEvent) => void, up: (event: PointerEvent) => void, cancel: () => void) {
  const second = (event: PointerEvent) => { if (event.pointerId !== pointerId) cancel() }
  const cancelled = () => cancel()
  window.addEventListener('pointerdown', second, true); window.addEventListener('pointermove', move, { passive: false })
  window.addEventListener('pointerup', up); window.addEventListener('pointercancel', cancelled); window.addEventListener('blur', cancelled)
  return () => {
    window.removeEventListener('pointerdown', second, true); window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', cancelled); window.removeEventListener('blur', cancelled)
  }
}
export function useLongPress(onLongPress?: (event: Press) => void) {
  const owner = useScope(), callback = useRef(onLongPress), state = useRef({ stop: () => {}, fired: false }).current
  callback.current = onLongPress
  useLayoutEffect(() => {
    const reset = () => { state.stop(); state.fired = false }
    const visibility = () => { if (document.hidden) reset() }
    const release = owner.onActive(active => { if (!active) reset() })
    document.addEventListener('visibilitychange', visibility)
    return () => { reset(); release(); document.removeEventListener('visibilitychange', visibility) }
  }, [owner])
  return {
    onPointerDown(event: Point) {
      state.stop(); state.fired = false
      if (!callback.current || event.button !== 0 || !event.isPrimary || !owner.active) return
      const target = event.currentTarget, id = event.pointerId, x = event.clientX, y = event.clientY
      let unlisten = () => {}, untimer = () => {}, release = () => {}
      const stop = () => { unlisten(); untimer(); release(); state.stop = () => {} }
      state.stop = stop
      unlisten = pointers(id, moved => { if (moved.pointerId === id && Math.hypot(moved.clientX - x, moved.clientY - y) > layout['drag-slop']) stop() }, ended => { if (ended.pointerId === id) stop() }, stop)
      untimer = owner.timeout(() => { stop(); state.fired = true; callback.current?.({ currentTarget: target, pointerId: id }) }, LONG_PRESS)
      release = owner.own(stop, 'work')
    },
    onContextMenu(event: JSX.TargetedMouseEvent<HTMLElement>) { if (callback.current) event.preventDefault() },
    onClickCapture(event: JSX.TargetedMouseEvent<HTMLElement>) { if (state.fired) { event.preventDefault(); event.stopPropagation(); state.fired = false } },
  }
}
export interface SwipeAction { key?: string; label: string; icon?: ComponentChildren; tone?: string; run(): void }
export function SwipeRow({ start = [], end = [], children }: { start?: SwipeAction[]; end?: SwipeAction[]; children?: ComponentChildren }) {
  const owner = useScope(), content = useRef<HTMLDivElement>(null), row = useRef<HTMLDivElement>(null)
  const state = useRef({ offset: 0, stop: () => {}, motion: undefined as Motion | undefined, start, end, dragged: false }).current
  state.start = start; state.end = end
  const place = (x: number) => { state.offset = x; if (content.current) content.current.style.transform = x ? `translateX(${x}px)` : ''; if (row.current) row.current.dataset.side = x > 0 ? 'start' : x < 0 ? 'end' : '' }
  useLayoutEffect(() => {
    const node = content.current!, sync = () => {
      const scrolling = [...node.querySelectorAll<HTMLElement>('*')].some(child => child.scrollWidth > child.clientWidth && /auto|scroll/.test(getComputedStyle(child).overflowX))
      row.current!.style.touchAction = scrolling ? 'auto' : 'pan-y'
    }
    sync()
    const resize = new ResizeObserver(sync), mutation = new MutationObserver(sync)
    resize.observe(node); mutation.observe(node, { childList: true, subtree: true, characterData: true })
    const release = owner.own(() => { resize.disconnect(); mutation.disconnect() })
    return release
  }, [owner])
  const closeActions = () => { row.current?.removeAttribute('data-actions-open'); row.current?.querySelector('.cx-swipe-more')?.setAttribute('aria-expanded', 'false') }
  const settle = (to: number, velocity = 0, cause: MotionCause = 'gesture') => {
    state.motion?.cancel()
    state.motion = spring(state.offset, to, {
      velocity,
      cause,
      kind: 'spatial-fast',
      onFrame: place,
      owner,
      element: content.current!,
    })
  }
  useLayoutEffect(() => { const stop = () => { state.stop(); state.motion?.cancel(); closeActions(); state.dragged = false; place(0) }; const release = owner.onActive(active => { if (!active) stop() }); return () => { stop(); release() } }, [owner])
  const down = (event: Point) => {
    state.stop()
    if (event.button !== 0 || !event.isPrimary || event.pointerType === 'mouse' || !owner.active) return
    if (event.clientX < layout['edge-swipe-zone'] || event.clientX > innerWidth - layout['edge-swipe-zone']) return
    // Scrollable horizontal content owns its axis before a row tries to acquire it.
    for (let node = event.target as HTMLElement; node && node !== row.current; node = node.parentElement!) {
      if (node.scrollWidth > node.clientWidth && /auto|scroll/.test(getComputedStyle(node).overflowX)) return
    }
    state.motion?.cancel(); state.dragged = false
    const id = event.pointerId, x = event.clientX, y = event.clientY, from = state.offset, width = content.current!.offsetWidth, speed = tracker()
    speed.add(x, event.timeStamp)
    const follow = (clientX: number) => {
      let next = from + clientX - x
      if (next > 0 && !state.start.length || next < 0 && !state.end.length) next = 0
      place(next)
    }
    let locked = false, unlisten = () => {}, release = () => {}
    const stop = () => { unlisten(); release(); row.current?.removeAttribute('data-full'); state.stop = () => {} }
    const cancel = () => { stop(); settle(0) }
    state.stop = stop
    unlisten = pointers(id, moved => {
      if (moved.pointerId !== id) return
      const dx = moved.clientX - x, dy = moved.clientY - y
      if (!locked) {
        if (Math.abs(dy) > layout['drag-slop'] && Math.abs(dy) >= Math.abs(dx)) { stop(); return }
        if (Math.abs(dx) < layout['drag-slop']) return
        locked = true; state.dragged = true
      }
      moved.preventDefault(); speed.add(moved.clientX, moved.timeStamp)
      follow(moved.clientX)
      row.current?.toggleAttribute('data-full', Math.abs(state.offset) >= width * layout['full-swipe-fraction'])
    }, ended => {
      if (ended.pointerId !== id) return
      speed.add(ended.clientX, ended.timeStamp); stop()
      if (!locked) return
      follow(ended.clientX)
      const actions = state.offset > 0 ? state.start : state.end, side = Math.sign(state.offset), velocity = speed.velocity(ended.timeStamp)
      if (Math.abs(state.offset) >= width * layout['full-swipe-fraction'] && actions[0]) { settle(0, velocity); actions[0].run(); return }
      const reveal = actions.length * layout['swipe-action-width']
      const open = Math.abs(state.offset) > reveal / 2 || Math.abs(velocity) > layout['fling-velocity'] && Math.sign(velocity) === side
      settle(open ? side * reveal : 0, velocity)
    }, cancel)
    release = owner.own(stop, 'work')
  }
  const buttons = (actions: SwipeAction[]) => actions.map(action => h('button', { key: action.key ?? action.label, type: 'button', class: 'cx-swipe-action type-caption', 'data-tone': action.tone, onClick: (event: MouseEvent) => { closeActions(); settle(0, 0, activationCause(event)); action.run() } }, action.icon, h('span', null, action.label)))
  return h('div', { class: 'cx-swipe', ref: row, onPointerDown: down },
    h('div', { class: 'cx-swipe-actions cx-swipe-start' }, buttons(start)), h('div', { class: 'cx-swipe-actions cx-swipe-end' }, buttons(end)),
    h('div', { class: 'cx-swipe-content', ref: content, onClickCapture: (event: MouseEvent) => { if (state.offset || state.dragged) { event.preventDefault(); event.stopPropagation(); state.dragged = false; settle(0, 0, activationCause(event)) } } }, children),
    (start.length || end.length) ? h('button', { type: 'button', class: 'cx-swipe-more', 'aria-label': 'Show row actions', 'aria-expanded': 'false', onClick: (event: MouseEvent) => { state.stop(); state.motion?.cancel(); place(0); const open = row.current!.toggleAttribute('data-actions-open'); (event.currentTarget as HTMLElement).setAttribute('aria-expanded', String(open)) } }, '⋯') : null)
}
